import { sortBy, keyBy, get } from "lodash";
import * as api from "./adapter";
import { convertItem, convertItems } from "./convertors/crewChange";
import { convertItems as convertTps } from "./convertors/timeplans";
import { getNoUserNameTextByStatus } from "../timeplans";
import { parseContentDispositionFilename } from "../parseContentDispositionFilename";

// CREW-1036 — the three crew-change print buttons. Rails 8's `send_file` sends
//   attachment; filename="X.xlsx"; filename*=UTF-8''X.xlsx
// and the name used to be cut with `.split("filename=")[1]`, which kept the
// `; filename*=…` twin: saved that way the file lost its extension and would
// not open. The shared parser reads either form; the fallback covers a
// response without the header.
const printFile = async (url, fallbackName) => {
  const response = await fetch(url, api.getDefaultOptions());
  const blob = await response.blob();

  return {
    uri: window.URL.createObjectURL(blob),
    name: parseContentDispositionFilename(response.headers.get("Content-Disposition")) || fallbackName,
  };
};

export default {
  index: async ({
    pagination,
    sort,
    filter: {
      state: { value: state },
      vessel,
      port,
      coordinator,
      owner,
    },
  }) => {
    const filter = state !== "all" ? { state } : {};

    if (vessel) filter.vessel_id = vessel.value;
    if (port) filter.port_id = port.value;
    if (coordinator) filter.coordinator = coordinator.value;
    if (owner) filter.owner = owner.value;

    const query = {
      include: "port,vessel,crew_lists",
      filter: { ...filter },
      page: { size: pagination.itemsPerPage, number: pagination.current },
    };

    // CREW-1042: only send `sort` once the user picks a column. With a
    // sort param always present, the BE's DEFAULT_ORDER (planned first,
    // nearest date on top) never fires — see CrewChangeResource#apply_sort.
    if (sort && sort.field)
      query["sort"] = `${sort.direction === "asc" ? "" : "-"}${sort.field}`;

    const { data: json } = await api.read("crew_changes", { query });

    return convertItems(json);
  },
  /**
   * show
   * @param id
   * @return {Object}
   */
  show: async (id) => {
    const { data: json } = await api.read(`/crew_changes/${id}`, {
      query: { include: "crew_lists,port,port_arrived,vessel" },
    });

    const {
      crewList: crewListInstances,
      timeplanIds,
      ...crewChange
    } = convertItem(json);

    const { data: timeplansJson } = await api.read("time_plans", {
      query: {
        include: "user,manning_scale",
        filter: { id: timeplanIds.join(",") },
      },
    });

    const convertedTimeplans = convertTps(timeplansJson);
    const timeplansById = keyBy(convertedTimeplans.data, "id");

    const crewList = crewListInstances.map((cl) => {
      const onTp = timeplansById[cl.attributes.sign_on_id];
      const offTp = timeplansById[cl.attributes.sign_off_id];

      return {
        id: cl.id,
        ...cl.attributes,
        rank: get(onTp || offTp, "msItem.attributes.title"),
        rank_priority: get(onTp || offTp, "msItem.attributes.crewing_priority"),
        on_sign_seaman: onTp
          ? getNoUserNameTextByStatus(onTp.status, onTp.userName)
          : undefined,
        off_sign_seaman: offTp
          ? getNoUserNameTextByStatus(offTp.status, offTp.userName)
          : undefined,
      };
    });

    const sortedCrewList = sortBy(crewList, "rank_priority");

    return {
      ...crewChange,
      crewList: sortedCrewList,
      timeplansById,
    };
  },
  /**
   * create
   * @param attributes
   * @returns {Promise}
   */
  create: async (
    { date, date_sign_on, vesselId, port, portArrived, state, author_id },
    crewlists
  ) => {
    const item = await api.create(
      {
        type: "crew_changes",
        attributes: {
          date,
          date_sign_on,
          // `state` is the `{value, label}` option object (sf-form
          // ComboField contract) — unwrap to the raw enum string
          // here.
          state: state.value,
          crewlists,
          author_id,
        },
        relationships: {
          port: { data: port ? { id: port.value, type: "ports" } : null },
          port_arrived: {
            data: portArrived ? { id: portArrived.value, type: "ports" } : null,
          },
          vessel: { data: { id: vesselId, type: "vessels" } },
        },
      },
      // Pull the same `included` docs the index endpoint
      // returns — `convertItem` below needs them to populate
      // `port`/`portArrived`/`vessel` labels and `crewList`.
      // Without `include` the response carries only relationship
      // linkage (id/type) and the row landed in the list with
      // null `port`/blank vessel labels even though the save
      // itself succeeded.
      { query: { include: "port,port_arrived,vessel,crew_lists" } },
    );

    // QA bug 6.3 follow-up (2026-05-19). The list rows in
    // `state.crewChanges.data` are stored in the flat shape
    // `convertItem` produces (`{id, date, prettyDate,
    // port: {value,label}, vessel: <name>, ...}`). The CRUD
    // saga's create/update handlers dispatch `data.data` from
    // here straight into the list reducer (`addItemToData` /
    // `updateItem`) — if we returned the raw JR envelope
    // `{id, type, attributes, relationships}` the row landed
    // without top-level `date`, and `BodyItem.classesForRow`
    // exploded on `tryParseDate(undefined).toDateString()` →
    // white-screened the list after every save. Convert the
    // response in-place so the saga's `data.data` is the same
    // shape every other row uses.
    return { ...item, data: { ...item.data, data: convertItem(item.data) } };
  },
  /**
   * update
   * @param id
   * @param attributes
   * @returns {Promise}
   */
  update: async (id, { date, date_sign_on, port, portArrived, state }) => {
    const resp = await api.update(
      {
        id,
        type: "crew_changes",
        attributes: {
          date,
          date_sign_on,
          // See note in `create` above — `state` is the
          // `{value, label}` option object from ComboField.
          state: state.value,
        },
        relationships: {
          port: { data: port ? { id: port.value, type: "ports" } : null },
          port_arrived: {
            data: portArrived ? { id: portArrived.value, type: "ports" } : null,
          },
        },
      },
      // Mirror the index endpoint's `include` so the row that
      // lands in the list keeps port / vessel / crew_lists
      // labels populated (see note in `create`).
      { query: { include: "port,port_arrived,vessel,crew_lists" } },
    );
    // See note on `create` above re: flat-shape convertor — the
    // CRUD saga's `updateItem` dispatches `data.data` straight
    // into the list reducer; without conversion the row landed
    // without top-level `date`/`prettyDate`/etc. and
    // `BodyItem.classesForRow` crashed on
    // `tryParseDate(undefined).toDateString()`.
    return { ...resp, data: { ...resp.data, data: convertItem(resp.data) } };
  },
  /**
   * delete
   * @returns {Promise<*>}
   */
  delete: (id) => api.destroy({ id, type: "crew_changes" }),

  printCrewList: (ccId) => printFile(`/api/print/crew_list?crew_change_id=${ccId}`, 'CrewList.xlsx'),

  printCrewRole: (ccId, type) =>
    printFile(`/api/print/crew_role?crew_change_id=${ccId}&type=${type}`, `CrewList_${type}.xlsx`),

  printEquipmentOrder: (ccId, mtxId) =>
    printFile(`/api/print/ppe_order?crew_change_id=${ccId}&owner_matrix_id=${mtxId}`, 'PPE_Order.xlsx'),
};
